import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Users, ChevronRight } from 'lucide-react';
import { recipes } from '../data/recipes';
import { cn } from '../utils/cn';

const RecipesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredRecipes = recipes.filter(recipe => 
    recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.keywords.some(keyword => keyword.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          Delicious Egg Recipes
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
          Discover amazing recipes to create with your perfectly boiled eggs. From classic favorites to gourmet creations, we have egg recipes for every occasion.
        </p>
        
        <div className="max-w-lg mx-auto relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search recipes..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      {filteredRecipes.length === 0 ? (
        <div className="text-center py-16">
          <h2 className="text-2xl font-medium text-gray-600 mb-4">No recipes found</h2>
          <p className="text-gray-500">Try a different search term or browse all recipes below.</p>
          <button 
            className="mt-6 px-6 py-2 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors"
            onClick={() => setSearchQuery('')}
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredRecipes.map((recipe) => (
            <div key={recipe.id} className="bg-white rounded-xl shadow-md overflow-hidden transition-transform hover:scale-[1.02]">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={recipe.image} 
                  alt={recipe.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 flex justify-between items-center">
                  <div className="flex space-x-3">
                    <span className="inline-flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-white">
                      <Clock className="w-3 h-3 mr-1" />
                      {recipe.prepTime} + {recipe.cookTime}
                    </span>
                    <span className="inline-flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-white">
                      <Users className="w-3 h-3 mr-1" />
                      Serves {recipe.servings}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h2 className="font-display text-xl font-semibold text-gray-800 mb-2 line-clamp-1">
                  {recipe.title}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {recipe.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {recipe.keywords.slice(0, 3).map((keyword, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {keyword}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/recipes/${recipe.slug}`}
                  className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  View Recipe
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Amazon Product Recommendations */}
      <section className="mt-16 bg-primary-50 rounded-2xl p-8">
        <h2 className="font-display text-2xl font-bold text-gray-800 mb-6 text-center">
          Essential Kitchen Tools for Perfect Eggs
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-display text-lg font-semibold text-gray-800 mb-3">
              Electric Egg Cooker
            </h3>
            <p className="text-gray-600 mb-4">
              Cook perfect eggs with the press of a button. Automatically makes hard, medium, or soft boiled eggs.
            </p>
            <a 
              href="https://www.amazon.com/s?k=Dash+Rapid+Egg+Cooker+Scrambled" 
              target="_blank" 
              rel="nofollow noopener noreferrer"
              className={cn(
                "block w-full text-center px-4 py-2 rounded-md",
                "bg-primary-500 text-white font-medium",
                "hover:bg-primary-600 transition-colors"
              )}
            >
              View on Amazon
            </a>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-display text-lg font-semibold text-gray-800 mb-3">
              Egg Timer Tool
            </h3>
            <p className="text-gray-600 mb-4">
              This ingenious egg timers changes color to indicate when your eggs are soft, medium, or hard boiled.
            </p>
            <a 
              href="https://www.amazon.com/s?k=egg+timer+for+boiling+eggs" 
              target="_blank" 
              rel="nofollow noopener noreferrer"
              className={cn(
                "block w-full text-center px-4 py-2 rounded-md",
                "bg-primary-500 text-white font-medium",
                "hover:bg-primary-600 transition-colors"
              )}
            >
              View on Amazon
            </a>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-display text-lg font-semibold text-gray-800 mb-3">
              Silicone Egg Poacher
            </h3>
            <p className="text-gray-600 mb-4">
              Create restaurant-quality poached eggs at home with these easy-to-use silicone poaching cups.
            </p>
            <a 
              href="https://www.amazon.com/s?k=Silicone+Egg+Poaching+Cups+Dishwasher" 
              target="_blank" 
              rel="nofollow noopener noreferrer"
              className={cn(
                "block w-full text-center px-4 py-2 rounded-md",
                "bg-primary-500 text-white font-medium",
                "hover:bg-primary-600 transition-colors"
              )}
            >
              View on Amazon
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecipesPage;